import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";

type SupportingContext = {
  kind: string;
  text: string;
  citation?: string;
  is_verbatim?: boolean;
};

type Hit = {
  doc_id: string;
  score: number;
  cxu_hash: string | null;
  text: string;
  rank: number;
  meta?: {
    engine?: string;
    match_type?: string;
    supporting_contexts?: SupportingContext[];
    knowledge_type?: string;
    classification?: string;
  };
};

type EngineResult = {
  results: Hit[];
  latency_ms: number;
  doc_ids: string[];
  tokens?: number;
  error: string | null;
};

type EngineJudgment = {
  context_relevancy: number;
  relevant_passages: string;
  context_sufficiency: number;
  missing: string;
  combined: number;
  tokens: number;
  quality_per_1k_tokens: number;
  answer: string;
};

type Judgment = {
  error?: string;
  per_engine?: Record<string, EngineJudgment>;
  pairwise?: { winner: string; passes: string[]; position_consistent: boolean };
  metric_winners?: Record<string, string | null>;
  winner?: string;
  model?: string;
};

type CompareResponse = {
  query: string;
  engines: Record<string, EngineResult>;
  comparison: {
    k: number;
    latency_ms: Record<string, number>;
    latency_winner: string | null;
    doc_overlap_jaccard?: number;
    only_in?: Record<string, string[]>;
  };
  judgment?: Judgment;
};

// `key` stays "cortiq" (wired to the engine + CSS); the customer-facing label is cortIQ.
const ENGINES = [
  { key: "cortiq", label: "cortIQ" },
  { key: "rag", label: "Straight RAG" },
] as const;

type BenchmarkMetrics = Record<string, number>;
type BenchmarkEngineMetrics = Record<string, BenchmarkMetrics>;
type BenchmarkData = {
  run_id: string;
  benchmark: string;
  sample: { n_docs: number; n_queries: number };
  k: number;
  metrics_computed: string[];
  by_engine: BenchmarkEngineMetrics;
  winner_delta: Record<string, { winner: string; delta: number } & Record<string, number>>;
  by_question_type: Record<string, { n_queries: number; metrics: BenchmarkEngineMetrics; winner_delta: Record<string, { winner: string }> }>;
  latency: Record<string, { p50_ms: number; p95_ms: number }>;
  vs_baseline: { note: string; published_baselines: Array<{ name: string; is_best?: boolean; MRR?: number; "Hits@10"?: number; "Hits@4"?: number }> };
};

const METRIC_DISPLAY: Record<string, string> = {
  "hits@10": "Hits@10",
  "hits@4": "Hits@4",
  mrr: "MRR",
  "map@10": "MAP@10",
  "recall@5": "Recall@5",
  "ndcg@5": "NDCG@5",
};

function MetricTable({
  metrics,
  byEngine,
  winnerDelta,
}: {
  metrics: string[];
  byEngine: BenchmarkEngineMetrics;
  winnerDelta: Record<string, { winner: string }>;
}) {
  const engines = Object.keys(byEngine);
  return (
    <table className="bm-table">
      <thead>
        <tr>
          <th>Metric</th>
          {engines.map((e) => <th key={e} className={e}>{e === "cortiq" ? "cortIQ" : "RAG"}</th>)}
          <th>Winner</th>
        </tr>
      </thead>
      <tbody>
        {metrics.map((m) => {
          // Round to 2dp before determining winner — sub-1% differences in retrieval
          // metrics are noise when the output is LLM context, not a final answer.
          const rounded = Object.fromEntries(
            engines.map((e) => {
              const v = byEngine[e]?.[m];
              return [e, typeof v === "number" ? Math.round(v * 100) / 100 : null];
            })
          );
          const vals = engines.map((e) => rounded[e]);
          const allSame = vals.every((v) => v !== null && v === vals[0]);
          const maxVal = vals.reduce<number | null>((acc, v) => (v !== null && (acc === null || v > acc) ? v : acc), null);
          const winners = allSame ? [] : engines.filter((e) => rounded[e] === maxVal);
          const winner = winners.length === 1 ? winners[0] : "tie";
          return (
            <tr key={m}>
              <td className="bm-metric">{METRIC_DISPLAY[m] ?? m}</td>
              {engines.map((e) => {
                const raw = byEngine[e]?.[m];
                return (
                  <td key={e} className={winner === e ? "bm-win" : ""}>
                    {typeof raw === "number" ? raw.toFixed(2) : "—"}
                    {winner === e ? " ★" : ""}
                  </td>
                );
              })}
              <td className={`pill ${winner}`}>{winner === "tie" ? "tie" : winner === "cortiq" ? "cortIQ" : winner}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function BenchmarkPanel() {
  const [data, setData] = useState<BenchmarkData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openType, setOpenType] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/benchmarks")
      .then((r) => r.json())
      .then((d) => {
        if (d.error) setErr(d.error);
        else setData(d as BenchmarkData);
      })
      .catch((e) => setErr(String(e)));
  }, []);

  if (err) return <div className="err bm-err">{err}</div>;
  if (!data) return <div className="bm-loading">Loading benchmark results…</div>;

  const metrics = data.metrics_computed;
  const types = Object.keys(data.by_question_type);

  return (
    <div className="bm-wrap">
      <div className="bm-header">
        <span className="bm-title">{data.benchmark}</span>
        <span className="bm-meta">
          {data.sample.n_queries} queries · {data.sample.n_docs} docs · k={data.k} ·{" "}
          <span className="bm-runid">run {data.run_id}</span>
        </span>
      </div>

      <div className="bm-section-label">Overall retrieval metrics</div>
      <MetricTable metrics={metrics} byEngine={data.by_engine} winnerDelta={data.winner_delta} />

      <div className="bm-latency">
        <span className="bm-section-label">Latency</span>
        {Object.entries(data.latency).map(([e, l]) => (
          <span key={e} className="bm-lat-item">
            <span className={`pill ${e}`}>{e === "cortiq" ? "cortIQ" : e}</span>
            {" "}p50 <b>{l.p50_ms?.toFixed(0) ?? "—"}ms</b> · p95 <b>{l.p95_ms?.toFixed(0) ?? "—"}ms</b>
          </span>
        ))}
      </div>

      <div className="bm-section-label">By question type</div>
      <div className="bm-types">
        {types.map((t) => {
          const slice = data.by_question_type[t];
          const open = openType === t;
          return (
            <div key={t} className={`bm-type ${open ? "open" : ""}`}>
              <button className="bm-type-head" onClick={() => setOpenType(open ? null : t)}>
                <span className="bm-type-name">{t}</span>
                <span className="bm-type-n">n={slice.n_queries}</span>
                <span className="bm-type-arrow">{open ? "▲" : "▼"}</span>
              </button>
              {open && (
                <MetricTable metrics={metrics} byEngine={slice.metrics} winnerDelta={slice.winner_delta} />
              )}
            </div>
          );
        })}
      </div>

      <details className="bm-baselines">
        <summary>Published baselines (reference band — not directly comparable)</summary>
        <p className="bm-baseline-note">{data.vs_baseline.note}</p>
        <table className="bm-table">
          <thead>
            <tr><th>System</th><th>MRR@10</th><th>MAP@10</th><th>Hits@10</th><th>Hits@4</th></tr>
          </thead>
          <tbody>
            {data.vs_baseline.published_baselines.map((b) => (
              <tr key={b.name} className={b.is_best ? "bm-best" : ""}>
                <td>{b.name}{b.is_best ? " ★" : ""}</td>
                <td>{b["MRR@10"] != null ? b["MRR@10"].toFixed(4) : "—"}</td>
                <td>{b["MAP@10"] != null ? b["MAP@10"].toFixed(4) : "—"}</td>
                <td>{b["Hits@10"] != null ? b["Hits@10"].toFixed(4) : "—"}</td>
                <td>{b["Hits@4"] != null ? b["Hits@4"].toFixed(4) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

const METRIC_LABELS: Record<string, string> = {
  context_relevancy: "Context relevancy",
  context_sufficiency: "Context sufficiency",
  combined: "Combined quality",
  quality_per_1k_tokens: "Quality / 1k tokens",
};

function JudgePanel({ judgment, engines }: { judgment: Judgment; engines: readonly { key: string; label: string }[] }) {
  if (judgment.error) return <div className="err">LLM judge: {judgment.error}</div>;
  const per = judgment.per_engine ?? {};
  const winners = judgment.metric_winners ?? {};
  const overall = judgment.winner ?? "tie";
  return (
    <div className="judge">
      <div className="judge-head">
        <span className="judge-title">Context quality (LLM judge)</span>
        <span className={`verdict pill ${overall}`}>
          {overall === "tie" ? "Verdict: tie" : `Winner: ${overall.toUpperCase()}`}
        </span>
        {judgment.pairwise && !judgment.pairwise.position_consistent ? (
          <span className="judge-note" title="the two position-swapped passes disagreed">pairwise inconsistent → tie</span>
        ) : null}
        <span className="judge-model">judge: {judgment.model}</span>
      </div>
      <table className="judge-table">
        <thead>
          <tr>
            <th>metric</th>
            {engines.map((e) => (
              <th key={e.key} className={e.key}>{e.key}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(METRIC_LABELS).map(([m, label]) => (
            <tr key={m}>
              <td>{label}</td>
              {engines.map((e) => (
                <td key={e.key} className={winners[m] === e.key ? "win" : ""}>
                  {per[e.key] ? per[e.key][m as keyof EngineJudgment] : "—"}
                  {winners[m] === e.key ? " ★" : ""}
                </td>
              ))}
            </tr>
          ))}
          {judgment.pairwise ? (
            <tr>
              <td>Pairwise answer A/B</td>
              {engines.map((e) => (
                <td key={e.key} className={judgment.pairwise!.winner === e.key ? "win" : ""}>
                  {judgment.pairwise!.winner === e.key ? "wins ★" : judgment.pairwise!.winner === "tie" ? "tie" : "loses"}
                </td>
              ))}
            </tr>
          ) : null}
        </tbody>
      </table>
      <div className="judge-answers">
        <p className="judge-note-ctx">
          cortIQ provides <strong>context</strong> for downstream LLM prompts — it does not generate answers.
          The answers below were produced by the judge model using each engine's retrieved context as input.
          "Missing" reflects what's absent from the <em>corpus</em>, not a retrieval failure.
        </p>
        {engines.map((e) =>
          per[e.key]?.answer ? (
            <details key={e.key}>
              <summary>{e.label} context → judge-generated answer</summary>
              <p>{per[e.key].answer}</p>
              {per[e.key].missing ? <p className="missing-note">Context coverage gap: {per[e.key].missing}</p> : null}
            </details>
          ) : null
        )}
      </div>
    </div>
  );
}

function Column({
  engineKey,
  label,
  data,
  onlyIn,
  isWinner,
}: {
  engineKey: string;
  label: string;
  data: EngineResult | undefined;
  onlyIn: string[];
  isWinner: boolean;
}) {
  const onlySet = new Set(onlyIn);
  return (
    <section className={`col ${engineKey} ${isWinner ? "winner" : ""}`}>
      <div className="head">
        <span className="name">{label}</span>
        {isWinner ? <span className="winbadge" title="LLM-judge overall winner">WINNER</span> : null}
        <span className="lat">{data ? `${data.latency_ms} ms` : ""}</span>
      </div>
      {data?.error ? (
        <div className="err">{data.error}</div>
      ) : !data || data.results.length === 0 ? (
        <div className="empty">No results.</div>
      ) : (
        <ol className="results">
          {data.results.map((h, i) => (
            <li className="hit" key={`${h.cxu_hash ?? h.doc_id}-${i}`}>
              <div className="row1">
                <span className="rank">{i + 1}</span>
                <span className={`doc ${onlySet.has(h.doc_id) ? "only" : ""}`} title={onlySet.has(h.doc_id) ? "only this engine returned this doc" : ""}>
                  {h.doc_id || "—"}
                </span>
                <span className="score" title={engineKey === "cortiq" ? "reciprocal rank (engine doesn't expose RRF scores)" : "cosine similarity"}>
                  {h.score.toFixed(4)}{engineKey === "cortiq" ? " rr" : " sim"}
                </span>
              </div>
              {h.text ? <div className="text">{h.text}</div> : null}
              {engineKey === "cortiq" && h.meta?.supporting_contexts?.length ? (
                <details className="supporting-ctx">
                  <summary>{h.meta.supporting_contexts.length} verbatim quote{h.meta.supporting_contexts.length !== 1 ? "s" : ""}</summary>
                  {h.meta.supporting_contexts.map((sc, j) => (
                    <blockquote key={j} className="quote">
                      <p>"{sc.text}"</p>
                      {sc.citation ? <cite>— {sc.citation}</cite> : null}
                    </blockquote>
                  ))}
                </details>
              ) : null}
            </li>
          ))}
        </ol>
      )}
      {data && !data.error ? (
        <div className="colfoot" title="prompt-context cost of this engine's returned text (cl100k tokens)">
          Tokens: <b>{(data.tokens ?? 0).toLocaleString()}</b>
        </div>
      ) : null}
    </section>
  );
}

function App() {
  const [tab, setTab] = useState<"compare" | "benchmark">("compare");
  const [query, setQuery] = useState("How should we think about pricing strategy?");
  const [k, setK] = useState(10);
  const [entityId, setEntityId] = useState("");
  const [judgeOn, setJudgeOn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [resp, setResp] = useState<CompareResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<"ok" | "down" | "unknown">("unknown");

  useEffect(() => {
    fetch("/api/healthz")
      .then((r) => setHealth(r.ok ? "ok" : "down"))
      .catch(() => setHealth("down"));
  }, []);

  async function run(e?: React.FormEvent) {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch("/api/compare", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ query, k, entity_id: entityId || null, judge: judgeOn }),
      });
      const body = await r.json();
      if (!r.ok) {
        setError(body.detail ?? `Request failed (${r.status})`);
        setResp(null);
      } else {
        setResp(body as CompareResponse);
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }

  const cmp = resp?.comparison;
  const onlyIn = cmp?.only_in ?? {};

  return (
    <div className="wrap">
      <header>
        <h1>cortIQ <span className="pronounce">(pronounced "Cortex")</span></h1>
        <span className="tagline">Others know business, cortIQ knows YOUR business</span>
        <span className="sub">cortIQ vs straight RAG — same query, side by side</span>
        <span className={`health ${health === "down" ? "bad" : ""}`}>
          backend: <b>{health === "ok" ? "connected" : health === "down" ? "unreachable" : "…"}</b>
        </span>
      </header>

      <div className="tabs">
        <button className={`tab ${tab === "compare" ? "active" : ""}`} onClick={() => setTab("compare")}>Live Compare</button>
        <button className={`tab ${tab === "benchmark" ? "active" : ""}`} onClick={() => setTab("benchmark")}>Benchmark</button>
      </div>

      {tab === "benchmark" && <BenchmarkPanel />}

      {tab === "compare" && <>
        <form className="bar" onSubmit={run}>
          <input
            aria-label="query"
            placeholder="Ask about pricing, sales, customers, growth…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <input aria-label="k" type="number" min={1} max={50} value={k} onChange={(e) => setK(Number(e.target.value))} />
          <input aria-label="entity_id" placeholder="entity_id (opt)" value={entityId} onChange={(e) => setEntityId(e.target.value)} />
          <label className="judge-toggle" title="LLM-judge the contexts (adds ~30-60s)">
            <input type="checkbox" checked={judgeOn} onChange={(e) => setJudgeOn(e.target.checked)} /> judge
          </label>
          <button type="submit" disabled={loading}>
            {loading ? (judgeOn ? "Running + judging…" : "Running…") : "Compare"}
          </button>
        </form>

        {error ? <div className="err">{error}</div> : null}

        {cmp ? (
          <div className="summary">
            <span><span className="k">k</span>{cmp.k}</span>
            {ENGINES.map((e) => (
              <span key={e.key}>
                <span className="k">{e.key} latency</span>
                <span className={`pill ${e.key}`}>{cmp.latency_ms[e.key]} ms</span>
              </span>
            ))}
            <span>
              <span className="k">faster</span>
              <span className={`pill ${cmp.latency_winner ?? ""}`}>{cmp.latency_winner ?? "—"}</span>
            </span>
            {typeof cmp.doc_overlap_jaccard === "number" ? (
              <span><span className="k">doc overlap</span>{(cmp.doc_overlap_jaccard * 100).toFixed(0)}%</span>
            ) : null}
          </div>
        ) : null}

        {resp?.judgment ? <JudgePanel judgment={resp.judgment} engines={ENGINES} /> : null}

        <div className="cols">
          {ENGINES.map((e) => (
            <Column
              key={e.key}
              engineKey={e.key}
              label={e.label}
              data={resp?.engines[e.key]}
              onlyIn={onlyIn[e.key] ?? []}
              isWinner={resp?.judgment?.winner === e.key}
            />
          ))}
        </div>

        <p className="foot">
          Highlighted <span className="doc only">doc</span> = returned by only this engine. Results come from{" "}
          <code>POST /compare</code> on the rag_compare API. Set <code>CORTIQ_MODE=fixture</code> to run without the live stack.
        </p>
      </>}
    </div>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
